import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { Button } from '@/shared/ui/button';
import { Pagination } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  agendaCacheAgeLabel,
  clearAgendaCache,
  scanFullAgendaCatalog,
} from '../api/use-campaign-agenda';
import {
  AGENDA_CHANNEL_FILTERS,
  AGENDA_FREQ_FILTERS,
  AGENDA_WINDOW_FILTERS,
} from '../model/filters';
import { groupAgendaByClient } from '../model/group-by-client';
import type { AgendaWindow } from '../model/next-run';
import { AgendaScanLoader } from './agenda-scan-loader';
import { AgendaClientCard } from './agenda-client-card';

const PAGE_SIZE = 25;
const AGENDA_QUERY_KEY = ['campaigns-admin', 'agenda', 'full'] as const;

/** Agenda de campanhas automatizadas de todos os clientes — aba de Observabilidade. */
export function CampaignAgendaPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [channel, setChannel] = useState('all');
  const [frequency, setFrequency] = useState('all');
  const [timeWindow, setTimeWindow] = useState<AgendaWindow>('today');
  const [includeInactive, setIncludeInactive] = useState(false);

  const catalogQuery = useQuery({
    queryKey: AGENDA_QUERY_KEY,
    queryFn: ({ signal }) => scanFullAgendaCatalog({ signal }),
    staleTime: 6 * 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    enabled: user?.role === 'Admin',
    retry: false,
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const groups = useMemo(
    () =>
      groupAgendaByClient(catalogQuery.data?.campaigns ?? [], {
        search: debouncedSearch,
        channel,
        frequency,
        window: timeWindow,
        includeInactive,
      }),
    [catalogQuery.data?.campaigns, debouncedSearch, channel, frequency, timeWindow, includeInactive],
  );

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, channel, frequency, timeWindow, includeInactive]);

  const pageRows = useMemo(() => {
    const start = pageIndex * PAGE_SIZE;
    return groups.slice(start, start + PAGE_SIZE);
  }, [groups, pageIndex]);

  const isScanning = catalogQuery.isFetching && !catalogQuery.data?.fromCache;
  const showLoader = catalogQuery.isLoading || (isScanning && !catalogQuery.data);

  const handleForceRescan = async () => {
    clearAgendaCache();
    await queryClient.resetQueries({ queryKey: AGENDA_QUERY_KEY });
    await queryClient.fetchQuery({
      queryKey: AGENDA_QUERY_KEY,
      queryFn: ({ signal }) => scanFullAgendaCatalog({ signal, force: true }),
    });
  };

  if (showLoader) {
    return <AgendaScanLoader />;
  }

  const fetchedAt = catalogQuery.data?.fetchedAt;
  const cacheHint = fetchedAt ? `${catalogQuery.data?.fromCache ? 'cache' : 'atualizado'} ${agendaCacheAgeLabel(fetchedAt)}` : null;

  return (
    <>

      <SelectableListLayout
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por cliente ou campanha…"
        selectAllId="agenda-select-all"
        itemCount={pageRows.length}
        allOnPageSelected={false}
        someOnPageSelected={false}
        onToggleAllOnPage={() => {}}
        hideSelection
        withFloatingBar={false}
        isLoading={false}
        isEmpty={groups.length === 0}
        emptyTitle="Nenhuma campanha na agenda"
        emptyDescription="Ajuste os filtros ou a janela de tempo."
        filtersRow={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={timeWindow} onValueChange={(v) => setTimeWindow(v as AgendaWindow)}>
              <SelectTrigger className="w-auto min-w-[10rem] gap-1.5 pr-3">
                <SelectValue placeholder="Janela" />
              </SelectTrigger>
              <SelectContent>
                {AGENDA_WINDOW_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="w-auto min-w-[9.5rem] gap-1.5 pr-3">
                <SelectValue placeholder="Canal" />
              </SelectTrigger>
              <SelectContent>
                {AGENDA_CHANNEL_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger className="w-auto min-w-[10rem] gap-1.5 pr-3">
                <SelectValue placeholder="Frequência" />
              </SelectTrigger>
              <SelectContent>
                {AGENDA_FREQ_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={includeInactive ? 'all' : 'active'}
              onValueChange={(v) => setIncludeInactive(v === 'all')}
            >
              <SelectTrigger className="w-auto min-w-[9rem] gap-1.5 pr-3">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Só ativas</SelectItem>
                <SelectItem value="all">Incluir inativas</SelectItem>
              </SelectContent>
            </Select>

            <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
              {cacheHint}
              <Button
                variant="outline"
                size="sm"
                disabled={catalogQuery.isFetching}
                onClick={() => void handleForceRescan()}
              >
                <RefreshCw className={`size-4 ${catalogQuery.isFetching ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </span>
          </div>
        }
        footer={
          groups.length > PAGE_SIZE ? (
            <Pagination
              page={pageIndex}
              pageSize={PAGE_SIZE}
              total={groups.length}
              onPageChange={setPageIndex}
            />
          ) : null
        }
      >
        {pageRows.map((group, index) => (
          <li key={group.clientId}>
            <AgendaClientCard
              group={group}
              index={index}
              onOpen={() =>
                navigate(`/agenda-campanhas/${encodeURIComponent(group.clientId)}`, {
                  state: {
                    clientName: group.clientName,
                    filters: {
                      channel,
                      frequency,
                      window: timeWindow,
                      includeInactive,
                    },
                  },
                })
              }
            />
          </li>
        ))}
      </SelectableListLayout>
    </>
  );
}
